/** Data-fetching hook for the attribution metrics endpoint (LH-114). */

import { useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { apiRequest, isApiError } from "@/shared/lib/apiClient";
import { queryKeys } from "@/shared/lib/queryKeys";

import type { AttributionResponse, MetricsRange } from "../lib/metricsTypes";
import { rangeToKey, rangeToQueryParams } from "../lib/metricsTypes";

const EMPTY_ATTRIBUTION: AttributionResponse = {
  rows: [],
  total_attributed_signups: 0,
  total_paid: 0,
};

interface UseAttributionResult {
  attribution: AttributionResponse | null;
  loading: boolean;
  error: string | null;
  range: MetricsRange;
  setRange: (range: MetricsRange) => void;
  refetch: () => void;
}

export function useAttribution(initialRange: MetricsRange = "30d"): UseAttributionResult {
  const [range, setRange] = useState<MetricsRange>(initialRange);
  const queryClient = useQueryClient();
  const rangeKey = rangeToKey(range);
  const qp = rangeToQueryParams(range);

  const query = useQuery({
    queryKey: queryKeys.metrics(`attribution:${rangeKey}`),
    queryFn: async () => {
      try {
        return await apiRequest<AttributionResponse>("GET", `/api/metrics/attribution?${qp}`);
      } catch (err) {
        if (isApiError(err) && err.status === 400) return EMPTY_ATTRIBUTION;
        throw err;
      }
    },
    staleTime: 60_000,
  });

  const refetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["metrics", `attribution:${rangeKey}`] });
  }, [queryClient, rangeKey]);

  return {
    attribution: query.data ?? null,
    loading: query.isLoading,
    error: query.error instanceof Error ? query.error.message : null,
    range,
    setRange,
    refetch,
  };
}
